import type { GridColDef } from "@mui/x-data-grid";

export type HeaderFilterColumnType =
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "dateTime"
  | "singleSelect";

/**
 * An operator option. `value` is the operator sent to the backend (must stay stable and
 * match what the `*FilterWithMuiGrid` endpoints expect). `messageId` is a key in
 * `locales/messages` resolved to a localized label at render time — operator labels must
 * never be hard-coded display strings here (module-level constants can't call `t()`).
 */
export type FilterOperatorOption = {
  value: string;
  messageId: string;
};

const STRING_OPERATORS: FilterOperatorOption[] = [
  { value: "contains", messageId: "Filter_op_contains" },
  { value: "equals", messageId: "Filter_op_equals" },
  { value: "startsWith", messageId: "Filter_op_startsWith" },
  { value: "endsWith", messageId: "Filter_op_endsWith" },
  { value: "isEmpty", messageId: "Filter_op_isEmpty" },
  { value: "isNotEmpty", messageId: "Filter_op_isNotEmpty" },
];

const NUMBER_OPERATORS: FilterOperatorOption[] = [
  { value: "=", messageId: "Filter_op_eq" },
  { value: "!=", messageId: "Filter_op_ne" },
  { value: ">", messageId: "Filter_op_gt" },
  { value: ">=", messageId: "Filter_op_gte" },
  { value: "<", messageId: "Filter_op_lt" },
  { value: "<=", messageId: "Filter_op_lte" },
  { value: "isEmpty", messageId: "Filter_op_isEmpty" },
  { value: "isNotEmpty", messageId: "Filter_op_isNotEmpty" },
];

const BOOLEAN_OPERATORS: FilterOperatorOption[] = [
  { value: "is", messageId: "Filter_op_is" },
];

const DATE_OPERATORS: FilterOperatorOption[] = [
  { value: "is", messageId: "Filter_op_is" },
  { value: "not", messageId: "Filter_op_not" },
  { value: "after", messageId: "Filter_op_after" },
  { value: "onOrAfter", messageId: "Filter_op_onOrAfter" },
  { value: "before", messageId: "Filter_op_before" },
  { value: "onOrBefore", messageId: "Filter_op_onOrBefore" },
  { value: "isEmpty", messageId: "Filter_op_isEmpty" },
  { value: "isNotEmpty", messageId: "Filter_op_isNotEmpty" },
];

const SINGLE_SELECT_OPERATORS: FilterOperatorOption[] = [
  { value: "is", messageId: "Filter_op_is" },
  { value: "not", messageId: "Filter_op_not" },
  { value: "isEmpty", messageId: "Filter_op_isEmpty" },
  { value: "isNotEmpty", messageId: "Filter_op_isNotEmpty" },
];

export function getDefaultOperator(
  type: GridColDef["type"] | undefined,
): string {
  return getOperatorsForType(type)[0]?.value ?? "contains";
}

export function getOperatorsForType(
  type: GridColDef["type"] | undefined,
): FilterOperatorOption[] {
  switch (type) {
    case "number":
      return NUMBER_OPERATORS;
    case "boolean":
      return BOOLEAN_OPERATORS;
    case "date":
    case "dateTime":
      return DATE_OPERATORS;
    case "singleSelect":
      return SINGLE_SELECT_OPERATORS;
    default:
      return STRING_OPERATORS;
  }
}

/**
 * The `messages` key for an operator's label. Resolve it to text with
 * `resolveFilterLabel(t, messageId)` from `filterFormat`.
 */
export function getOperatorMessageId(
  type: GridColDef["type"] | undefined,
  operator: string,
): string {
  const match = getOperatorsForType(type).find((op) => op.value === operator);
  return match?.messageId ?? operator;
}

export function operatorNeedsValue(operator: string): boolean {
  return operator !== "isEmpty" && operator !== "isNotEmpty";
}

export function isFilterableColumn(col: GridColDef): boolean {
  return col.filterable !== false && Boolean(col.field);
}

export function normalizeColumnType(
  type: GridColDef["type"] | undefined,
): HeaderFilterColumnType {
  if (
    type === "number" ||
    type === "boolean" ||
    type === "date" ||
    type === "dateTime" ||
    type === "singleSelect"
  ) {
    return type;
  }
  return "string";
}

export function showOperatorMenu(type: GridColDef["type"] | undefined): boolean {
  return getOperatorsForType(type).length > 1;
}
